import React, {useEffect, useState, useContext} from "react";
import {useParams, useNavigate, Link} from "react-router-dom";
import {profilesApi} from "../api/profiles";
import {battlePersonApi} from "../api/battlePerson";
import {presenceApi} from "../api/presence";
import { getProfileStats } from "../api/duelStats";
import { getTournaments } from "../api/tournaments";
import { UserContext } from "../context/UserContext";
import HrnbContainer, {HrnbRow, HrnbCol} from "../components/controls/HrnbContainer";
import HrnbCard from "../components/controls/HrnbCard";
import HrnbButton from "../components/controls/HrnbButton";
import {HrnbTabs, HrnbTab} from "../components/controls/HrnbTabs";
import HrnbImage from "../components/controls/HrnbImage";
import HrnbLoader from "../components/controls/HrnbLoader";
import HrnbAlert from "../components/controls/HrnbAlert";
import OnlineStatusIndicator from "../components/controls/OnlineStatusIndicator";
import DuelRating from "../components/duels/DuelRating";
import TournamentCard from "../components/duels/TournamentCard";
import '../css/pages/ProfilePage.css';
import HrnbLink from "./controls/HrnbLink";

const ProfilePage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { currentUser } = useContext(UserContext);

    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState("duels");
    const [battleHistory, setBattleHistory] = useState([]);
    const [stats, setStats] = useState({
        totalBattles: 0,
        wonBattles: 0,
        rating: 0,
        joinedDate: null
    });

    // Дуэли и рейтинг
    const [duelStats, setDuelStats] = useState(null);
    const [duelRating, setDuelRating] = useState(null);

    // Достижения
    const [achievements, setAchievements] = useState([]);
    const [achievementsLoading, setAchievementsLoading] = useState(false);

    // Турниры
    const [tournaments, setTournaments] = useState([]);
    const [tournamentsLoading, setTournamentsLoading] = useState(false);

    // Онлайн-статус пользователя
    const [userPresence, setUserPresence] = useState({ isOnline: false, lastActivity: null });

    useEffect(() => {
        fetchProfile();
        fetchBattleHistory();
        fetchDuelStats();
        fetchAchievements();
        fetchTournaments();
    }, [id]);

    // Загрузка онлайн-статуса пользователя
    useEffect(() => {
        if (!profile?.userId) return;

        const fetchPresence = async () => {
            try {
                const presence = await presenceApi.getStatus(profile.userId);
                setUserPresence({ isOnline: presence.isOnline, lastActivity: presence.lastActivity });
            } catch (err) {
                console.error('[ProfilePage] Failed to fetch presence:', err);
            }
        };

        fetchPresence();

        // TODO: Добавить WebSocket подписку при необходимости
        return () => {
            // Отписка от WebSocket (если будет добавлена)
        };
    }, [profile?.userId]);

    const fetchProfile = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await profilesApi.get(id);
            const profileData = response.data || response;
            setProfile(profileData);
            
            // Вычисляем статистику
            calculateStats(profileData);
        } catch (error) {
            console.error("Error fetching profile:", error);
            setError("Не удалось загрузить профиль");
        } finally {
            setLoading(false);
        }
    };

    const fetchBattleHistory = async () => {
        try {
            // NOTE: API reference для GET /api/battle_person не предусматривает фильтрацию по профилю.
            // Загружаем записи и фильтруем на клиенте по publicProfileId.
            const data = await battlePersonApi.list({ size: 100 });
            const history = Array.isArray(data) ? data : (data?.content || []);
            const userHistory = history.filter(b => b.publicProfileId === id || b.publicProfile === id);
            setBattleHistory(userHistory);

            // Вычисляем статистику по баттлам
            const wonCount = userHistory.filter(b => b.applicationStatus === 'APPROVED').length;
            setStats(prev => ({
                ...prev,
                totalBattles: userHistory.length,
                wonBattles: wonCount
            }));
        } catch (error) {
            console.error("Error fetching battle history:", error);
        }
    };

    const fetchDuelStats = async () => {
        try {
            const statsData = await getProfileStats(id);
            setDuelStats(statsData);

            // Получаем рейтинг из statsData или через getPlayerRank
            if (statsData.rating) {
                setDuelRating({
                    rating: statsData.rating,
                    rank: statsData.rank || 0,
                    wins: statsData.wins || 0,
                    losses: statsData.losses || 0,
                    draws: statsData.draws || 0,
                    totalDuels: statsData.totalDuels || 0,
                    winRate: statsData.winRate || 0,
                    recentForm: statsData.recentForm || []
                });
            }
        } catch (error) {
            console.error("Error fetching duel stats:", error);
        }
    };

    const fetchAchievements = async () => {
        setAchievementsLoading(true);
        try {
            // TODO: Добавить API метод для получения достижений пользователя
            // Пока используем заглушку
            setAchievements([]);
        } catch (error) {
            console.error("Error fetching achievements:", error);
        } finally {
            setAchievementsLoading(false);
        }
    };

    const fetchTournaments = async () => {
        setTournamentsLoading(true);
        try {
            // Получаем турниры, где участвует этот пользователь
            // Используем getTournaments с фильтрацией на клиенте
            const allData = await getTournaments({ size: 50 });
            const allTournaments = allData.content || allData || [];
            const userTournaments = allTournaments.filter(t =>
                t.participants?.some(p => String(p.profileId) === String(id))
            );
            setTournaments(userTournaments);
        } catch (error) {
            console.error("Error fetching tournaments:", error);
        } finally {
            setTournamentsLoading(false);
        }
    };

    const calculateStats = (profileData) => {
        // Пример расчёта статистики (можно улучшить по мере необходимости)
        setStats({
            totalBattles: profileData.totalBattles || 0,
            wonBattles: profileData.wonBattles || 0,
            rating: profileData.rating || 0,
            joinedDate: profileData.createdAt || profileData.joinedDate
        });
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Не указано';
        return new Date(dateString).toLocaleDateString('ru-RU', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const getRatingColor = (rating) => {
        if (rating >= 2000) return '#dc3545'; // Красный - мастер
        if (rating >= 1500) return '#fd7e14'; // Оранжевый - эксперт
        if (rating >= 1000) return '#20c997'; // Зелёный - продвинутый
        return '#0d6efd'; // Синий - новичок
    };

    const getStatusBadge = (applicationStatus) => {
        const badges = {
            PENDING: { color: 'warning', text: 'На рассмотрении' },
            APPROVED: { color: 'success', text: 'Участник' },
            REJECTED: { color: 'danger', text: 'Отклонён' },
            WITHDRAWN: { color: 'secondary', text: 'Отозван' }
        };
        return badges[applicationStatus] || { color: 'secondary', text: applicationStatus };
    };

    if (loading) {
        return (
            <HrnbContainer className="profile-page">
                <div className="profile-page__loading">
                    <HrnbLoader size="lg" />
                </div>
            </HrnbContainer>
        );
    }

    if (error || !profile) {
        return (
            <HrnbContainer className="profile-page">
                <HrnbAlert variant="danger" onClose={() => navigate('/profiles')}>
                    {error || 'Профиль не найден'}
                </HrnbAlert>
            </HrnbContainer>
        );
    }

    const isOwnProfile = currentUser && (currentUser.id === profile.userId || profile.userId === id);

    return (
        <HrnbContainer className="profile-page">
            {/* Header Section */}
            <HrnbRow className="profile-page__header">
                <HrnbCol xs={12}>
                    <HrnbCard className="profile-page__card">
                        <div className="profile-page__content">
                            <div className="profile-page__avatar-section">
                                <HrnbImage
                                    src={profile.avatarUrl || profile.avatar || '/default-avatar.png'}
                                    alt={profile.displayName}
                                    className="profile-page__avatar"
                                />
                            </div>
                            
                            <div className="profile-page__info">
                                <h1 className="profile-page__name">{profile.displayName}</h1>

                                {/* Онлайн-статус */}
                                <div className="profile-page__online-status">
                                    <OnlineStatusIndicator
                                        isOnline={userPresence.isOnline}
                                        lastActivity={userPresence.lastActivity}
                                        size="md"
                                        showLabel={true}
                                    />
                                </div>

                                {profile.bio && (
                                    <p className="profile-page__bio">{profile.bio}</p>
                                )}
                                
                                <div className="profile-page__meta">
                                    <span className="profile-page__joined">
                                       Joined: {formatDate(profile.createdAt || profile.joinedDate)}
                                    </span>
                                </div>

                                {isOwnProfile && (
                                    <div className="profile-page__actions">
                                        <HrnbButton 
                                            variant="primary"
                                            onClick={() => navigate('/cabinet')}
                                        >
                                            Редактировать профиль
                                        </HrnbButton>
                                    </div>
                                )}
                            </div>
                        </div>
                    </HrnbCard>
                </HrnbCol>
            </HrnbRow>

            {/* Stats Section */}
            <HrnbRow className="profile-page__stats">
                <HrnbCol xs={6} md={3}>
                    <HrnbCard className="profile-page__stat-card">
                        <div className="profile-page__stat-value">{stats.totalBattles}</div>
                        <div className="profile-page__stat-label">Всего баттлов</div>
                    </HrnbCard>
                </HrnbCol>
                <HrnbCol xs={6} md={3}>
                    <HrnbCard className="profile-page__stat-card">
                        <div className="profile-page__stat-value">{stats.wonBattles}</div>
                        <div className="profile-page__stat-label">Участвовал</div>
                    </HrnbCard>
                </HrnbCol>
                <HrnbCol xs={6} md={3}>
                    <HrnbCard className="profile-page__stat-card">
                        <div 
                            className="profile-page__stat-value"
                            style={{ color: getRatingColor(stats.rating) }}
                        >
                            {stats.rating}
                        </div>
                        <div className="profile-page__stat-label">Рейтинг</div>
                    </HrnbCard>
                </HrnbCol>
                <HrnbCol xs={6} md={3}>
                    <HrnbCard className="profile-page__stat-card">
                        <div className="profile-page__stat-value">
                            {Math.round((stats.wonBattles / Math.max(stats.totalBattles, 1)) * 100)}%
                        </div>
                        <div className="profile-page__stat-label">Win Rate</div>
                    </HrnbCard>
                </HrnbCol>
            </HrnbRow>

            {/* Duel Rating Section */}
            {duelRating && (
                <HrnbRow className="profile-page__duel-rating">
                    <HrnbCol xs={12}>
                        <HrnbCard>
                            <div className="profile-page__section-header">
                                <h2>Рейтинг дуэлей</h2>
                                <Link to={`/duels/stats/${id}`}>
                                    <HrnbButton variant="outline" size="sm">Подробнее</HrnbButton>
                                </Link>
                            </div>
                            <DuelRating rating={duelRating} size="lg" />
                        </HrnbCard>
                    </HrnbCol>
                </HrnbRow>
            )}

            {/* Tabs Section */}
            <HrnbRow>
                <HrnbCol xs={12}>
                    <HrnbCard>
                        <HrnbTabs value={activeTab} onChange={setActiveTab}>
                            <HrnbTab label="Дуэли" value="duels" />
                            <HrnbTab label="Достижения" value="achievements" />
                            <HrnbTab label="Турниры" value="tournaments" />
                            <HrnbTab label="История баттлов" value="battles" />
                            <HrnbTab label="Социальные ссылки" value="social" />
                        </HrnbTabs>
                        
                        <div className="profile-page__tab-content">
                            {/* Дуэли */}
                            {activeTab === "duels" && (
                                <div className="profile-page__tab-pane">
                                    {duelStats ? (
                                        <div className="profile-page__duel-stats">
                                            <div className="profile-page__duel-overview">
                                                <div className="profile-page__duel-stat-item">
                                                    <span className="profile-page__duel-stat-value">{duelStats.totalDuels || 0}</span>
                                                    <span className="profile-page__duel-stat-label">Всего дуэлей</span>
                                                </div>
                                                <div className="profile-page__duel-stat-item">
                                                    <span className="profile-page__duel-stat-value">{duelStats.wins || 0}</span>
                                                    <span className="profile-page__duel-stat-label">Победы</span>
                                                </div>
                                                <div className="profile-page__duel-stat-item">
                                                    <span className="profile-page__duel-stat-value">{duelStats.losses || 0}</span>
                                                    <span className="profile-page__duel-stat-label">Поражения</span>
                                                </div>
                                                <div className="profile-page__duel-stat-item">
                                                    <span className="profile-page__duel-stat-value">{duelStats.draws || 0}</span>
                                                    <span className="profile-page__duel-stat-label">Ничьи</span>
                                                </div>
                                            </div>

                                            {duelStats.audioDuels > 0 && (
                                                <div className="profile-page__duel-type">
                                                    <h4>Аудио дуэли</h4>
                                                    <p>Побед: {duelStats.audioWins || 0} | Поражений: {duelStats.audioLosses || 0}</p>
                                                </div>
                                            )}

                                            {duelStats.textDuels > 0 && (
                                                <div className="profile-page__duel-type">
                                                    <h4>Текстовые дуэли</h4>
                                                    <p>Побед: {duelStats.textWins || 0} | Поражений: {duelStats.textLosses || 0}</p>
                                                </div>
                                            )}

                                            <Link to={`/duels/stats/${id}`}>
                                                <HrnbButton variant="primary">Полная статистика</HrnbButton>
                                            </Link>
                                        </div>
                                    ) : (
                                        <p className="profile-page__no-data">Статистика дуэлей пока отсутствует</p>
                                    )}
                                </div>
                            )}

                            {/* Достижения */}
                            {activeTab === "achievements" && (
                                <div className="profile-page__tab-pane">
                                    {achievementsLoading ? (
                                        <HrnbLoader size="sm" />
                                    ) : achievements.length > 0 ? (
                                        <div className="profile-page__achievements">
                                            {achievements.map((achievement) => (
                                                <HrnbCard key={achievement.id} className="profile-page__achievement-card">
                                                    <div className="profile-page__achievement-content">
                                                        <div className="profile-page__achievement-icon">
                                                            {achievement.icon || '🏆'}
                                                        </div>
                                                        <div className="profile-page__achievement-info">
                                                            <h4 className="profile-page__achievement-title">{achievement.name}</h4>
                                                            <p className="profile-page__achievement-description">{achievement.description}</p>
                                                            {achievement.progress && (
                                                                <div className="profile-page__achievement-progress">
                                                                    <div className="profile-page__achievement-progress-bar">
                                                                        <div
                                                                            className="profile-page__achievement-progress-fill"
                                                                            style={{ width: `${(achievement.progress.current / achievement.progress.required) * 100}%` }}
                                                                        />
                                                                    </div>
                                                                    <span className="profile-page__achievement-progress-text">
                                                                        {achievement.progress.current} / {achievement.progress.required}
                                                                    </span>
                                                                </div>
                                                            )}
                                                            <div className="profile-page__achievement-meta">
                                                                <span className={`profile-page__achievement-rarity rarity-${achievement.rarity?.toLowerCase()}`}>
                                                                    {achievement.rarity || 'COMMON'}
                                                                </span>
                                                                <span className="profile-page__achievement-category">
                                                                    {achievement.category || 'Общее'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </HrnbCard>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="profile-page__no-data">Достижения пока отсутствуют</p>
                                    )}
                                </div>
                            )}
                            
                            {/* Турниры */}
                            {activeTab === "tournaments" && (
                                <div className="profile-page__tab-pane">
                                    {tournamentsLoading ? (
                                        <HrnbLoader size="sm" />
                                    ) : tournaments.length > 0 ? (
                                        <div className="profile-page__tournaments">
                                            {tournaments.map((tournament) => (
                                                <TournamentCard
                                                    key={tournament.id}
                                                    tournament={tournament}
                                                    compact={true}
                                                />
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="profile-page__no-data">Турниры пока отсутствуют</p>
                                    )}
                                </div>
                            )}

                            {/* История баттлов */}
                            {activeTab === "battles" && (
                                <div className="profile-page__tab-pane">
                                    {battleHistory.length > 0 ? (
                                        <div className="profile-page__battle-history">
                                            {battleHistory.map((battle, index) => {
                                                const statusBadge = getStatusBadge(battle.applicationStatus);
                                                return (
                                                    <HrnbCard key={index} className="profile-page__battle-card">
                                                        <div className="profile-page__battle-header">
                                                            <h4 className="profile-page__battle-title">
                                                                {battle.battleTitle || `Баттл #${battle.battleId || battle.id}`}
                                                            </h4>
                                                            <span className={`profile-page__battle-status badge-${statusBadge.color}`}>
                                                                {statusBadge.text}
                                                            </span>
                                                        </div>
                                                        <div className="profile-page__battle-info">
                                                            <span>Роль: {battle.role || 'Участник'}</span>
                                                            <span>Дата: {formatDate(battle.appliedAt || battle.createdAt)}</span>
                                                        </div>
                                                    </HrnbCard>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <p className="profile-page__no-data">История баттлов пуста</p>
                                    )}
                                </div>
                            )}

                            {/* Социальные ссылки */}
                            {activeTab === "social" && (
                                <div className="profile-page__tab-pane">
                                    {profile.socialLinks && profile.socialLinks.length > 0 ? (
                                        <div className="profile-page__social-links">
                                            {profile.socialLinks.map((link, index) => (
                                                <HrnbCard key={index} className="profile-page__social-card">
                                                    <div className="profile-page__social-content">
                                                        <span className="profile-page__social-platform">{link.platform}</span>
                                                        <HrnbLink href={link.url} target="_blank" rel="noopener noreferrer">
                                                            {link.url}
                                                        </HrnbLink>
                                                    </div>
                                                </HrnbCard>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="profile-page__no-data">Социальные ссылки отсутствуют</p>
                                    )}
                                </div>
                            )}
                        </div>
                    </HrnbCard>
                </HrnbCol>
            </HrnbRow>
        </HrnbContainer>
    );
};

export default ProfilePage;
